import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Users, DollarSign, Rocket, Sparkles, Handshake, TrendingUp, AlertTriangle } from "lucide-react";

const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;

export default function AdminOverview() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [ai, setAi] = useState(null);
  useEffect(() => { api.get("/admin/overview").then((r) => setD(r.data)).catch(() => setErr(true)); }, []);
  useEffect(() => { api.get("/admin/action-items").then((r) => setAi(r.data)).catch(() => {}); }, []);
  if (err) return <div className="py-20 text-center text-muted-foreground" data-testid="admin-overview-error">Couldn't load the overview. Your session may have expired — try signing in again.</div>;
  if (!d) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const actions = ai ? [
    ai.pending_affiliate_apps > 0 && { label: `${ai.pending_affiliate_apps} affiliate application${ai.pending_affiliate_apps > 1 ? "s" : ""} awaiting review`, to: "/admin/affiliates" },
    ai.affiliates_no_stripe > 0 && { label: `${ai.affiliates_no_stripe} affiliate${ai.affiliates_no_stripe > 1 ? "s" : ""} without payouts set up`, to: "/admin/affiliates" },
    ai.trials_expiring_3d > 0 && { label: `${ai.trials_expiring_3d} trial${ai.trials_expiring_3d > 1 ? "s" : ""} expiring in 3 days`, to: "/admin/subscriptions" },
  ].filter(Boolean) : [];


  const cards = [
    { label: "Total users", value: d.users_total, sub: `+${d.new_7d} this week`, icon: Users, tone: "text-[#24468C] bg-[#24468C]/10" },
    { label: "Monthly revenue", value: `$${d.mrr_usd.toFixed(2)}`, sub: `${d.active_paid} paid subs`, icon: DollarSign, tone: "text-green-600 bg-green-50" },
    { label: "On trial", value: d.trialing, sub: "trialing now", icon: Rocket, tone: "text-[#8a6410] bg-[#E8A93C]/15" },
    { label: "CV analysis leads", value: d.leads_total, sub: `${d.leads_converted} converted`, icon: Sparkles, tone: "text-purple-600 bg-purple-50" },
    { label: "Affiliates", value: d.affiliates, sub: "approved", icon: Handshake, tone: "text-[#24468C] bg-[#24468C]/10" },
    { label: "Commission owed", value: money(d.affiliate_pending_cents), sub: "pending payout", icon: TrendingUp, tone: "text-amber-600 bg-amber-50" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Overview</h1>
        <p className="text-muted-foreground text-sm">Your Jobsmith platform at a glance.</p>
      </div>
      {actions.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="action-needed-strip">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-amber-800 mb-2"><AlertTriangle className="h-4 w-4" /> Action needed</p>
          <div className="flex flex-wrap gap-2">
            {actions.map((a, i) => (
              <Link key={a.to + a.label} to={a.to} className="inline-flex items-center rounded-lg bg-white border border-amber-200 px-3 py-1.5 text-sm text-amber-900 hover:bg-amber-100 transition-colors" data-testid={`action-item-${i}`}>{a.label} →</Link>
            ))}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <Card key={c.label} className="border-zinc-200 shadow-sm" data-testid={`kpi-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{c.label}</p>
                  <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${c.tone}`}><Icon className="h-4 w-4" strokeWidth={1.75} /></div>
                </div>
                <p className="font-heading text-3xl font-bold mt-2">{c.value}</p>
                <p className="text-xs text-muted-foreground mt-1">{c.sub}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
